import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { XIcon, UploadIcon, FileIcon } from 'lucide-react';
import { knowledgeBaseDocumentApi } from '../../api/knowledge-base';
import { VisaTypeSelector } from './VisaTypeSelector';
import { TagInput } from './TagInput';
import { KnowledgeBaseContentType, CONTENT_TYPE_LABELS } from '../../types/knowledge-base';

interface FileUploaderProps {
  defaultFolderId?: string;
  onClose: () => void;
  onUploaded: () => void;
}

export const FileUploader: React.FC<FileUploaderProps> = ({
  defaultFolderId,
  onClose,
  onUploaded,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [contentType, setContentType] = useState<KnowledgeBaseContentType>(
    KnowledgeBaseContentType.TEMPLATE
  );
  const [tags, setTags] = useState<string[]>([]);
  const [visaTypes, setVisaTypes] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const uploadedFile = acceptedFiles[0];
      setFile(uploadedFile);
      if (!title) {
        setTitle(uploadedFile.name.replace(/\.[^/.]+$/, ''));
      }
    }
  }, [title]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'text/markdown': ['.md'],
    },
    maxFiles: 1,
  });

  const handleUpload = async () => {
    if (!file) {
      alert('Please select a file');
      return;
    }

    if (!title.trim()) {
      alert('Please enter a title');
      return;
    }

    try {
      setUploading(true);
      console.log('[FileUploader] Starting upload:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        title,
        contentType,
        tags,
        visaTypes,
        folderId: defaultFolderId,
      });

      const result = await knowledgeBaseDocumentApi.uploadFile(file, {
        title,
        contentType,
        tags,
        visaTypes,
        folderId: defaultFolderId,
      });

      console.log('[FileUploader] Upload successful:', result);
      alert('File uploaded successfully!');
      onUploaded();
    } catch (error: any) {
      console.error('[FileUploader] Upload failed:', error);
      const errorMessage = error?.message || error?.toString() || 'Failed to upload file';
      alert(`Upload failed: ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Upload File</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* File Drop Zone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">File</label>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragActive
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <input {...getInputProps()} />
              {file ? (
                <div className="flex items-center justify-center space-x-3">
                  <FileIcon className="w-8 h-8 text-blue-500" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
              ) : (
                <div>
                  <UploadIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-600">
                    Drag and drop a file here, or click to select
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Supported: PDF, DOCX, TXT, MD (Max 500MB)
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Document title"
            />
          </div>

          {/* Content Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content Type</label>
            <select
              value={contentType}
              onChange={(e) => setContentType(e.target.value as KnowledgeBaseContentType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {Object.values(KnowledgeBaseContentType).map((type) => (
                <option key={type} value={type}>
                  {CONTENT_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
          </div>

          {/* Visa Types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Visa Types</label>
            <VisaTypeSelector value={visaTypes} onChange={setVisaTypes} />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
            <TagInput value={tags} onChange={setTags} />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={uploading || !file}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
};
